import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { PACK } from '../../../consts/packs';
import { SharedReduxState } from '../../../reducers/types';
import { PREFERENCE } from '../../UserPreferences/const/preferences';
import useDashboardsAccess from '../../../../../frontend/dashboard/hooks/useDashboardsAccess';
import { useDeviceManagerAccess } from '../../../../../frontend/device_management/shared/hooks/useDeviceManagerAccess';
import useAssetManagerAccess from '../../../../../frontend/equipment_management/shared/hooks/useAssetManagerAccess';
import useRiskMonitorAccess from '../../../../../frontend/suv/shared/hooks/useRiskMonitorAccess';
import useShipmentManagementAccess from '../../../../../frontend/shipment_manager/hooks/useShipmentManagementAccess';
import { useDefaultPages } from '../../../hooks/useDefaultPages';
import useAdminAccess from '../../../../../frontend/admin/shared/hooks/useAdminAccess';
import useScGoAccess from '../../../../../frontend/shipment_manager/modules/ShipmentConnectGo/hooks/useScGoAccess';
import { SC_GO_ROOT_PATH } from '../../../../../frontend/shipment_manager/modules/ShipmentConnectGo/const';
import { useInventoryPageAccess } from '../../../../../frontend/inventory_management/shared/hooks/useInventoryPageAccess';

export const useRedirectLink = () => {
  const userPreferences = useSelector((state: SharedReduxState) =>
    state.userPreferences
  );
  const userId = useSelector((state: SharedReduxState) => state.auth.user!.id);

  const { hasDashboardsAccess } = useDashboardsAccess();
  const { hasDMAccess } = useDeviceManagerAccess();
  const { hasAMAccess } = useAssetManagerAccess();
  const { hasRMAccess } = useRiskMonitorAccess();
  const { hasSMAppAccess } = useShipmentManagementAccess();
  const { getDefaultPageByName } = useDefaultPages();
  const { hasAdminPageAccess } = useAdminAccess();
  const { hasScGoAccess } = useScGoAccess();
  const { hasInventoryManagementAccess } = useInventoryPageAccess();

  return useMemo(() => {
    const defaultApp = userPreferences[PREFERENCE.DEFAULT_APP];

    const defaultLandingPage = getDefaultPageByName(defaultApp);

    const redirectLinks = [
      {
        link: '',
        pack: PACK.dashboard,
        hasAccess: hasDashboardsAccess()
      },
      {
        link: '',
        pack: PACK.suv,
        hasAccess: hasRMAccess()
      },
      {
        link: 'shipments',
        pack: PACK.simpl2,
        hasAccess: hasSMAppAccess()
      },
      // Users whose only access is Shipment Connect Go should land on its home page
      {
        link: SC_GO_ROOT_PATH,
        pack: PACK.shipmentManager,
        hasAccess: hasScGoAccess()
      },
      {
        link: '',
        pack: PACK.deviceManagement,
        hasAccess: hasDMAccess()
      },
      {
        link: '',
        pack: PACK.equipment,
        hasAccess: hasAMAccess()
      },
      {
        link: '',
        pack: PACK.inventoryManagement,
        hasAccess: hasInventoryManagementAccess()
      },
      {
        link: '',
        pack: PACK.admin,
        hasAccess: hasAdminPageAccess()
      },
    ];

    if (defaultLandingPage) {
      redirectLinks.unshift({
        link: defaultLandingPage.link,
        pack: defaultLandingPage.pack,
        hasAccess: defaultLandingPage.hasAccess
      });
    }

    const redirectLink = redirectLinks.find(link => link.hasAccess);

    if (redirectLink) {
      return {
        link: redirectLink.link,
        pack: redirectLink.pack,
      };
    }

    // users/:id/* is not permission-guarded, unlike the users summary route
    return {
      link: `users/${userId}/settings`,
      pack: PACK.userManagement,
    };
  }, [userPreferences, userId]);
};

